import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search as SearchIcon, Filter, Users, MapPin, Trophy, X } from 'lucide-react';

interface Team {
  id: string;
  name: string;
  sport: string;
  location: string;
  logo: string;
  description: string;
  achievements: number;
  members: number;
  verified: boolean;
  recruiting: boolean;
}

const mockTeams: Team[] = [
  {
    id: '1',
    name: 'Phoenix Flyers',
    sport: 'Basketball',
    location: 'Phoenix, AZ',
    logo: 'https://images.pexels.com/photos/3621104/pexels-photo-3621104.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    description: 'Professional women\'s basketball team competing in the national league.',
    achievements: 12,
    members: 15,
    verified: true,
    recruiting: true
  },
  {
    id: '2',
    name: 'Seattle Storm',
    sport: 'Soccer',
    location: 'Seattle, WA',
    logo: 'https://images.pexels.com/photos/3621105/pexels-photo-3621105.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    description: 'Premier women\'s soccer club with a focus on youth development.',
    achievements: 8,
    members: 22,
    verified: true,
    recruiting: false
  },
  {
    id: '3',
    name: 'Miami Waves',
    sport: 'Volleyball',
    location: 'Miami, FL',
    logo: 'https://images.pexels.com/photos/3621106/pexels-photo-3621106.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    description: 'Competitive beach volleyball team with multiple national titles.',
    achievements: 15,
    members: 12,
    verified: true,
    recruiting: true
  }
];

interface TeamFilters {
  sport?: string;
  location?: string;
  recruiting?: boolean;
  verified?: boolean;
}

const Teams = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [filteredTeams, setFilteredTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<TeamFilters>({});

  useEffect(() => {
    document.title = 'Teams - FemSportNet';
    
    // Simulate API call
    const fetchTeams = async () => {
      try {
        setIsLoading(true);
        // In a real app, this would be an API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        setTeams(mockTeams);
        setFilteredTeams(mockTeams);
      } catch (error) {
        console.error('Error fetching teams:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeams();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, filters, teams]);

  const applyFilters = () => {
    let result = [...teams];
    
    // Apply search term
    if (searchTerm) {
      result = result.filter(team => 
        team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.sport.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sport filter
    if (filters.sport) {
      result = result.filter(team => 
        team.sport.toLowerCase().includes(filters.sport!.toLowerCase())
      );
    }
    
    // Apply location filter
    if (filters.location) {
      result = result.filter(team => 
        team.location.toLowerCase().includes(filters.location!.toLowerCase())
      );
    }
    
    // Apply recruiting filter
    if (filters.recruiting !== undefined) {
      result = result.filter(team => team.recruiting === filters.recruiting);
    }
    
    // Apply verified filter
    if (filters.verified !== undefined) {
      result = result.filter(team => team.verified === filters.verified);
    }
    
    setFilteredTeams(result);
  };

  const handleFilterChange = (key: keyof TeamFilters, value: any) => {
    setFilters({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen py-8 transition-colors duration-200">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Discover Teams
            </h1>
            <p className="text-gray-600 dark:text-gray-300 max-w-3xl">
              Find and connect with sports teams from around the world. Explore opportunities and join a team that matches your aspirations.
            </p>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-8 transition-colors duration-200">
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
              <div className="relative flex-grow mb-4 md:mb-0">
                <input
                  type="text"
                  placeholder="Search by team name, sport, or location"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-2 pl-10 pr-4 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
                />
                <SearchIcon className="absolute left-3 top-2.5 text-gray-400" size={18} />
              </div>
              
              <button
                onClick={toggleFilters}
                className="flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <Filter size={18} className="mr-2" />
                <span>Filters</span>
              </button>
            </div>
            
            {/* Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Sport
                  </label>
                  <input
                    type="text"
                    placeholder="Filter by sport"
                    value={filters.sport || ''}
                    onChange={(e) => handleFilterChange('sport', e.target.value || undefined)}
                    className="w-full py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    placeholder="Filter by location"
                    value={filters.location || ''}
                    onChange={(e) => handleFilterChange('location', e.target.value || undefined)}
                    className="w-full py-2 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={filters.recruiting || false}
                      onChange={(e) => handleFilterChange('recruiting', e.target.checked || undefined)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mr-2"
                    />
                    <span>Currently recruiting</span>
                  </label>
                  
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={filters.verified || false}
                      onChange={(e) => handleFilterChange('verified', e.target.checked || undefined)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mr-2"
                    />
                    <span>Verified teams only</span>
                  </label>
                </div>
                
                <div className="md:col-span-3 flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                  >
                    <X size={16} className="mr-1" />
                    <span>Clear filters</span>
                  </button>
                </div>
              </div>
            )}
            
            {/* Active Filters */}
            {Object.keys(filters).length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
                {filters.sport && (
                  <div className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-full text-sm flex items-center">
                    Sport: {filters.sport}
                    <button
                      onClick={() => handleFilterChange('sport', undefined)}
                      className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                
                {filters.location && (
                  <div className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-full text-sm flex items-center">
                    Location: {filters.location}
                    <button
                      onClick={() => handleFilterChange('location', undefined)}
                      className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                
                {filters.recruiting && (
                  <div className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-full text-sm flex items-center">
                    Recruiting
                    <button
                      onClick={() => handleFilterChange('recruiting', undefined)}
                      className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                
                {filters.verified && (
                  <div className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-full text-sm flex items-center">
                    Verified only
                    <button
                      onClick={() => handleFilterChange('verified', undefined)}
                      className="ml-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Results */}
          {isLoading ? (
            <div className="flex justify-center my-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-center justify-between">
                <p className="text-gray-600 dark:text-gray-300">
                  {filteredTeams.length} {filteredTeams.length === 1 ? 'team' : 'teams'} found
                </p>
              </div>
              
              {filteredTeams.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTeams.map(team => (
                    <Link
                      key={team.id}
                      to={`/teams/${team.id}`}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden transition-all duration-200 hover:shadow-md transform hover:-translate-y-1"
                    >
                      <div className="h-48 relative">
                        <img 
                          src={team.logo} 
                          alt={team.name} 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-xl font-semibold text-white mb-1">
                            {team.name}
                          </h3>
                          <div className="flex items-center text-white/90 text-sm">
                            <MapPin size={14} className="mr-1" />
                            {team.location}
                          </div>
                        </div>
                        {team.verified && (
                          <div className="absolute top-4 right-4 bg-blue-500 rounded-full w-6 h-6 flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      
                      <div className="p-4">
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                          <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                            {team.sport}
                          </span>
                          {team.recruiting && (
                            <span className="ml-2 text-green-500 dark:text-green-400">
                              • Recruiting
                            </span>
                          )}
                        </div>
                        
                        <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2 mb-4">
                          {team.description}
                        </p>
                        
                        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center">
                            <Users size={16} className="mr-1" />
                            <span>{team.members} members</span>
                          </div>
                          <div className="flex items-center">
                            <Trophy size={16} className="mr-1" />
                            <span>{team.achievements} achievements</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                  <div className="flex justify-center mb-4">
                    <Users size={48} className="text-gray-400" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                    No teams found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Try adjusting your search or filters to find what you're looking for
                  </p>
                  <button
                    onClick={clearFilters}
                    className="bg-green-500 hover:bg-green-600 text-white font-medium px-4 py-2 rounded-md transition-colors duration-200"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Teams;